<template>
  <div>
    <div v-if="!isRestrictedLocationPanel" class="subtitle2 mt-8 mb-4">
      {{ $t("modals.addAccessPermissions.restrict.allowAccessBy.title") }}
    </div>
    <v-select
      v-if="!isRestrictedLocationPanel"
      v-model="localValue.type"
      class="restriction-select"
      data-testid="access-restriction-modal-restriction-type-select"
      :label="$t('modals.addAccessPermissions.restrict.allowAccessBy.label')"
      :placeholder="$t('modals.addAccessPermissions.restrict.allowAccessBy.label')"
      :items="policyTypes"
      :rules="[required('Please select permission type')]"
      variant="outlined"
      :return-object="false"
    >
      <template #selection="{ item }">
        {{ $t(`modals.addAccessPermissions.restrict.types.${item.value}`) }}
      </template>
      <template v-slot:item="{ props, item }">
        <v-list-item
          v-bind="props"
          :data-testid="`access-restriction-modal-restriction-restrict-by-${item.value}`"
          :title="$t(`modals.addAccessPermissions.restrict.types.${item.value}`)"
        >
        </v-list-item>
      </template>
    </v-select>

    <div v-if="isRestrictedLocationPanel" class="subtitle2 mb-4">
      {{ $t("modals.addAccessRestriction.restrict.ip.title") }}
    </div>
    <template v-if="isRestrictionByCountry">
      <div class="subtitle2 mb-4">
        {{ $t("modals.addAccessPermissions.restrict.country.title") }}
      </div>
      <v-btn-toggle
        v-model="restrictBy"
        color="primary"
        class="mb-5"
        mandatory
        density="compact"
        selected-class="active-btn"
      >
        <v-btn value="country" class="toggle-btn mr-2" rounded="xl" variant="outlined">
          {{ $t("modals.addAccessPermissions.restrict.allCountries") }}
        </v-btn>
        <v-btn value="state" class="toggle-btn" rounded="xl" variant="outlined">
          {{ $t("modals.addAccessPermissions.restrict.usaStates") }}
        </v-btn>
      </v-btn-toggle>

      <v-autocomplete
        v-model="localValue.allowedLocations"
        item-value="countryKey"
        item-title="name"
        :items="countriesOrStates"
        :label="
          localValue.allowedLocations.length
            ? $t('modals.addAccessPermissions.restrict.country.label')
            : ''
        "
        :placeholder="$t('modals.addAccessPermissions.restrict.country.placeholder')"
        maxlength="150"
        variant="outlined"
        multiple
        chips
        closable-chips
        menu-icon="$triangle"
        :rules="[atLeastOneIsRequired()]"
        clear-on-select
        :no-data-text="$t('modals.addAccessPermissions.restrict.country.noData')"
        data-testid="access-restriction-modal-country-select"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip
            v-bind="props"
            :closable="true"
            variant="flat"
            size="default"
            close-icon="$closeCircle"
            :text="getCountryOrStateName(item.value)"
            color="indigo-faint"
          ></v-chip>
        </template>
        <template #item="{ item, props }">
          <v-list-item v-bind="props" :title="item.title" />
        </template>
      </v-autocomplete>
    </template>
    <template v-if="isRestrictionByIP">
      <v-combobox
        v-model.trim="localValue.allowedIps"
        :aria-label="$t('modals.addAccessPermissions.restrict.ip.label')"
        :label="
          localValue.allowedIps.length ? $t('modals.addAccessPermissions.restrict.ip.label') : ''
        "
        :placeholder="ipPlaceholder"
        :rules="[atLeastOneIsRequired(), ipRule]"
        maxlength="150"
        variant="outlined"
        :multiple="true"
        :chips="true"
        :closable-chips="true"
        clearable
        clear-icon="$x"
        hide-details="auto"
        data-testid="access-restriction-modal-add-ip-addresses-input"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip
            v-bind="props"
            variant="flat"
            size="default"
            close-icon="$closeCircle"
            :color="getChipColor(item.value, Patterns.IP)"
            :text="item.title"
          ></v-chip>
        </template>
      </v-combobox>
      <v-text-field
        v-model="localValue.description"
        class="mt-6"
        variant="outlined"
        data-testid="access-restriction-modal-description-input"
        :placeholder="$t('modals.addAccessPermissions.restrict.ipDescription.placeholder')"
        :aria-label="$t('modals.addAccessPermissions.restrict.ipDescription.placeholder')"
      ></v-text-field>
    </template>
    <template v-if="isRestrictionByTime">
      <div class="subtitle2">{{ $t("modals.addAccessPermissions.restrict.time.title") }}</div>
      <v-combobox
        v-model="localValue.allowedDays"
        data-testid="days-combobox"
        class="mt-3 coro-scrollable-combobox"
        :label="$t('modals.addAccessPermissions.restrict.time.days')"
        :aria-label="$t('modals.addAccessPermissions.restrict.time.days')"
        :placeholder="$t('modals.addAccessPermissions.restrict.time.days')"
        variant="outlined"
        multiple
        no-filter
        hide-no-data
        :return-object="false"
        item-title="name"
        item-value="value"
        :items="daysOptions"
      >
        <template #item="{ props, item }">
          <v-list-item v-bind="props" title="">
            <template v-slot:prepend="{ isActive }">
              <v-checkbox :model-value="isActive"> </v-checkbox>
            </template>
            <span class="ml-1">{{ item.raw.name }}</span>
          </v-list-item>
        </template>
      </v-combobox>
      <div class="d-flex">
        <v-select
          class="mr-4 time-select"
          variant="outlined"
          v-model="localValue.allowedStartTime"
          :items="timeOptions"
          :label="$t('modals.addAccessPermissions.restrict.time.from')"
          :placeholder="$t('modals.addAccessPermissions.restrict.time.from')"
        ></v-select>
        <v-select
          class="time-select"
          variant="outlined"
          v-model="localValue.allowedEndTime"
          :items="timeOptions"
          :label="$t('modals.addAccessPermissions.restrict.time.to')"
          :placeholder="$t('modals.addAccessPermissions.restrict.time.to')"
        ></v-select>
      </div>
    </template>

    <template v-if="isRestrictionByIP || isRestrictionByCountry || isRestrictionByTime">
      <div class="subtitle2 mt-8 mb-4">
        {{ $t("modals.addAccessPermissions.remediation.title") }}
      </div>
      <v-select
        v-model="localValue.remediation"
        :items="remediationOptions"
        data-testid="access-restriction-modal-remediation-select"
        variant="outlined"
      >
        <template #selection="{ item }">
          <span
            v-if="item.title"
            :data-testid="`access-restriction-modal-remediation-chip-${item.title}`"
            >{{ $t(`modals.addAccessPermissions.remediation.types.${item.title}`) }}</span
          >
        </template>
        <template #item="{ item, props }">
          <v-list-item
            v-bind="props"
            :title="$t(`modals.addAccessPermissions.remediation.types.${item.title}`)"
            :data-testid="`access-restriction-modal-remediation-${item.title}`"
          >
          </v-list-item>
        </template>
      </v-select>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { countriesList, usaStatesList } from "@/constants/countries";
import type {
  AccessPermissionItem,
  AccessPermissionRemediation,
  AccessPermissionRestrictionBy,
} from "@/_store/cloud-apps/access-permissions.module";
import { atLeastOneIsRequired, required } from "@/_helpers/validators";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import uniqBy from "lodash/uniqBy";
import { getCountryOrStateName } from "@/_helpers/devices";
import { Patterns } from "@coronet/cui";
import { AccessPermissionLocationPanel, Service } from "@/constants/cloud-apps";
import { DayOfWeek } from "@/constants/policies.ts";
import { useI18n } from "vue-i18n";

const generateHalfHourTimeSlots = (): string[] => {
  const timeSlots: string[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (const minutes of [0, 30]) {
      const hh = hour.toString().padStart(2, "0");
      const mm = minutes.toString().padStart(2, "0");
      timeSlots.push(`${hh}:${mm}`);
    }
  }

  return timeSlots;
};

const HALF_HOUR_TIME_SLOTS: readonly string[] = Object.freeze(generateHalfHourTimeSlots());

export default defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<AccessPermissionItem>,
      required: true,
    },
    locationPanel: {
      type: String as PropType<AccessPermissionLocationPanel>,
      default: AccessPermissionLocationPanel.PERMITTED,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const restrictBy = ref<AccessPermissionRestrictionBy>("country");
    const localValue = ref<AccessPermissionItem>(cloneDeep(props.modelValue));
    const isRestrictedLocationPanel = computed(() => {
      return props.locationPanel === AccessPermissionLocationPanel.RESTRICTED;
    });

    watch(
      () => props.modelValue,
      (value) => {
        if (!isEqual(value, localValue.value)) {
          localValue.value = cloneDeep(value);
        }
      },
      { deep: true }
    );

    watch(
      localValue,
      (value) => {
        if (!isEqual(value, props.modelValue)) {
          emit("update:modelValue", value);
        }
      },
      { deep: true }
    );

    const isRestrictionByCountry = computed(() => {
      return localValue.value.type === "country";
    });

    const isRestrictionByIP = computed(() => {
      return isRestrictedLocationPanel.value || localValue.value.type === "ip";
    });

    const isRestrictionByTime = computed(() => {
      return localValue.value.type === "time";
    });

    const ipPlaceholder = computed(() => {
      return isRestrictedLocationPanel.value
        ? i18n.t("modals.addAccessPermissions.restrict.ip.placeholderRestricted")
        : i18n.t("modals.addAccessPermissions.restrict.ip.placeholder");
    });

    const remediationOptions = computed<AccessPermissionRemediation[]>(() => {
      return localValue.value.serviceName === Service.BOX
        ? ["none", "suspend"]
        : ["none", "suspend", "signin"];
    });

    const availableCountries = Object.entries(countriesList).map(([key, value]) => ({
      countryKey: key,
      name: value,
    }));

    const availableStates = Object.entries(usaStatesList).map(([key, value]) => ({
      countryKey: key,
      name: value,
    }));

    const countriesOrStates = computed(() => {
      return restrictBy.value === "country"
        ? uniqBy(availableCountries, "countryKey")
        : uniqBy(availableStates, "countryKey");
    });

    const getChipColor = (value: string, pattern: RegExp) => {
      const isValid = pattern.test(value);
      return isValid ? "indigo-faint" : "error";
    };

    const ipRule = (values: string[]) => {
      for (const value of values) {
        if (!Patterns.IP.test(value)) {
          return i18n.t("validations.ip");
        }
      }

      return true;
    };

    // TODO add "time" when time sensitive access permissions are implemented
    const policyTypes: AccessPermissionRestrictionBy[] = ["country", "ip"];

    const daysOptions = Object.values(DayOfWeek).map((day, index) => ({
      name: i18n.t(`daysOfWeek.${day}`),
      value: index,
    }));

    return {
      atLeastOneIsRequired,
      countriesOrStates,
      getChipColor,
      getCountryOrStateName,
      isRestrictionByCountry,
      isRestrictionByIP,
      isRestrictedLocationPanel,
      ipPlaceholder,
      timeOptions: HALF_HOUR_TIME_SLOTS,
      isRestrictionByTime,
      ipRule,
      Patterns,
      policyTypes,
      remediationOptions,
      required,
      restrictBy,
      localValue,
      daysOptions,
    };
  },
});
</script>
